// components/SearchBar.tsx
import React, { useRef } from 'react';
import { FaSearch, FaPlus } from 'react-icons/fa';

interface SearchBarProps {
  query: string;
  results: any[];
  isSearching: boolean;
  showResults: boolean;
  onQueryChange: (query: string) => void;
  onResultSelect: (result: any) => void;
  isPickingLocation?: boolean;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  query,
  results,
  isSearching,
  showResults,
  onQueryChange,
  onResultSelect,
  isPickingLocation = false,
}) => {
  const searchInputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="w-full p-1">
      <div className="relative">
        <div className="flex items-center bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="pl-3 pr-2 text-gray-400">
            <FaSearch />
          </div>
          <input
            ref={searchInputRef}
            type="text"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder={isPickingLocation ? "Search for a location..." : "Search for a house or place..."}
            className="flex-1 py-2 px-2 outline-none text-sm"
            onFocus={() => {
              if (results.length > 0) {
                // Show results on focus
              }
            }}
            onBlur={() => {
              setTimeout(() => {
                // Hide results after delay
              }, 200);
            }}
          />
          {isSearching && (
            <div className="pr-3">
              <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
            </div>
          )}
        </div>
        
        {showResults && results.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg max-h-96 overflow-y-auto z-[1000]">
            {results.map((result, index) => (
              <button
                key={index}
                onClick={() => onResultSelect(result)}
                className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors flex items-start gap-3"
              >
                <FaPlus className="text-blue-500 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-800">
                    {result.name || result.display_name.split(',')[0]}
                  </p>
                  <p className="text-xs text-gray-500 line-clamp-1">{result.display_name}</p>
                  <p className="text-xs text-blue-600 mt-1">
                    📍 {parseFloat(result.lat).toFixed(4)}, {parseFloat(result.lon).toFixed(4)}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
        
        {showResults && query.length >= 2 && results.length === 0 && !isSearching && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg p-4 text-center text-gray-500 text-sm">
            No places found. Try a different search term.
          </div>
        )}
      </div>
    </div>
  );
};